import { api, downloadFile } from '@/shared/api/client'
import { url, type QueryValue } from '@/shared/lib/query'
import type {
  AgentAssignmentRequest, BoundaryImportRequest, ChangeUnitStatusRequest, CodeAvailability,
  CreateUnitRequest, GeoJsonObject, GeographySummary, ImportJobResponse, ImportRequest,
  ImportTemplateSpec, MoodAssessmentRequest, NearbyResponse, PageResponse, ReserveRequest,
  ReserveResponse, RiskAssessmentRequest, UnitDetailResponse, UnitResponse, UnitStatisticRequest,
  UnitTreeNode, UnitTypeInfo, UpdateUnitRequest,
} from '@/shared/api/types'

export interface UnitFilters {
  q: string
  type: string[]
  status: string[]
  parentId: string
  region: string
  sort: string
  /** Intelligence filters, served against `geography_unit_overlay`. */
  riskLevel: string[]
  hasAgent: string
  assessed: string
  turnoutMin: string
  turnoutMax: string
  moodMin: string
  moodMax: string
}

export const EMPTY_UNIT_FILTERS: UnitFilters = {
  q: '', type: [], status: [], parentId: '', region: '', sort: '',
  riskLevel: [], hasAgent: '', assessed: '', turnoutMin: '', turnoutMax: '',
  moodMin: '', moodMax: '',
}

/** Sort keys the backend accepts. `risk` orders by severity, not alphabetically. */
export const UNIT_SORT_KEYS = [
  'name', 'code', 'depth', 'createdAt', 'turnout', 'mood', 'risk', 'agentCoverage',
] as const

export function unitParams(f: UnitFilters): Record<string, QueryValue> {
  return {
    q: f.q,
    type: f.type,
    status: f.status,
    parentId: f.parentId,
    region: f.region,
    sort: f.sort,
    riskLevel: f.riskLevel,
    hasAgent: f.hasAgent,
    assessed: f.assessed,
    turnoutMin: f.turnoutMin,
    turnoutMax: f.turnoutMax,
    moodMin: f.moodMin,
    moodMax: f.moodMax,
  }
}

// ── Units ───────────────────────────────────────────────────────────────────

export const listUnits = (f: UnitFilters, page = 0, size = 50) =>
  api.get<PageResponse<UnitResponse>>(
    url('/geography/units', { ...unitParams(f), page, size }),
  )

export const getUnit = (id: string) =>
  api.get<UnitDetailResponse>(`/geography/units/${id}`)

export const listChildren = (id: string) =>
  api.get<UnitResponse[]>(`/geography/units/${id}/children`)

export const searchUnits = (q: string) =>
  api.get<UnitResponse[]>(url('/geography/units/search', { q }))

export const nearbyUnits = (lat: number, lon: number, radius: number) =>
  api.get<NearbyResponse[]>(url('/geography/units/nearby', { lat, lon, radius }))

/**
 * Returns a bare map server-side, so there is no generated type for it.
 * `GeographySummary` in shared/api/types mirrors the real shape.
 */
export const geographySummary = () => api.get<GeographySummary>('/geography/summary')

export const createUnit = (body: CreateUnitRequest) =>
  api.post<UnitResponse>('/geography/units', body)

/**
 * The hierarchy and geometry rules, served. This replaces the client-side copy —
 * `UNIT_TYPE_ORDER`, `childTypeOf` and `geometryKindOf` remain only as the offline
 * fallback and for types known before a row exists.
 */
export const unitTypes = () => api.get<UnitTypeInfo[]>('/geography/unit-types')

/** Is this code free? `reason` is `TAKEN` or `LTREE_COLLISION`. */
export const codeAvailable = (unitType: string, code: string, parentId?: string) =>
  api.get<CodeAvailability>(url('/geography/units/code-available', { unitType, code, parentId }))

/**
 * Bootstrap the root. With an empty database there is no COUNTRY, and every other
 * create requires a `parentId` — so this is the only way in. Not in the plan;
 * the backend added it. See docs/PHASE2-VERIFICATION.md C4.
 */
export const createCountry = () =>
  api.post<UnitResponse>('/geography/units/country')

/** PATCH requires `If-Match` carrying the version; a missing header is a 400. */
export const updateUnit = (id: string, version: number, body: UpdateUnitRequest) =>
  api.patch<UnitResponse>(`/geography/units/${id}`, body, {
    headers: { 'If-Match': String(version) },
  })

export const changeUnitStatus = (id: string, body: ChangeUnitStatusRequest) =>
  api.post<UnitResponse>(`/geography/units/${id}/status`, body)

// ── Tree ────────────────────────────────────────────────────────────────────

/**
 * Lazy by design — never request the whole 46k hierarchy. `depth` is how many
 * levels below `rootId` to materialise; the explorer asks for one at a time and
 * merges children in as branches open.
 */
export const unitTree = (rootId?: string, depth = 1) =>
  api.get<UnitTreeNode[]>(url('/geography/tree', { rootId, depth }))

// ── Boundary ────────────────────────────────────────────────────────────────

/**
 * `simplifyTolerance` is applied server-side at READ time — the stored geometry
 * stays exact. Degrees, so ~0.001 is roughly 100m at the equator.
 */
export const getBoundary = (id: string, simplifyTolerance?: number) =>
  api.get<GeoJsonObject>(url(`/geography/units/${id}/boundary`, { simplifyTolerance }))

/**
 * The GeoJSON goes in the REQUEST BODY. The plan specified a presigned upload
 * through `storage`, but the backend shipped a direct POST — so there is no
 * `boundaryFileId` and no original-file provenance.
 * See docs/PHASE2-VERIFICATION.md C2.
 */
export const uploadBoundary = (id: string, geoJson: GeoJsonObject) =>
  api.post<UnitResponse>(`/geography/units/${id}/boundary`, geoJson)

/**
 * Remove a boundary. Clears the polygon, the area and the file id, and keeps the
 * centroid — flipping `centroidSource` to `MANUAL` — so a map does not lose its pin
 * because someone deleted a bad shape.
 */
export const deleteBoundary = (id: string) =>
  api.del<UnitResponse>(`/geography/units/${id}/boundary`)

/**
 * Every child geometry of one level in ONE request — polygons for boundary-based
 * types, points for stations. This is what a choropleth needs; fetching them one
 * unit at a time is 1,450 requests for a county's wards.
 *
 * `parentId` is a **descendant** predicate (`path <@`), so a county id with
 * `type=WARD` returns that county's wards — which is the whole point of the endpoint.
 *
 * Simplification happens in PostGIS, so the response is a fraction of the stored
 * size. `cap` is clamped server-side to 2,000; asking for more is silently reduced.
 *
 * The response is cached `private, max-age=300`, so the browser will serve it without
 * a request for five minutes. Pass a changing `nonce` to force a fresh fetch after an
 * upload — there is no other way to bypass the HTTP cache from `fetch` without also
 * disabling it for every other caller.
 */
export const unitFeatures = (p: {
  type: string
  parentId?: string
  bbox?: string
  simplifyTolerance?: number
  status?: string
  cap?: number
  nonce?: number
}) => api.get<GeoJsonObject>(url('/geography/features', { ...p }))

// ── Import ──────────────────────────────────────────────────────────────────

export const startImport = (body: ImportRequest) =>
  api.post<ImportJobResponse>('/geography/import', body)

export const importJob = (jobId: string) =>
  api.get<ImportJobResponse>(`/geography/import/${jobId}`)

export const applyImport = (jobId: string) =>
  api.post<void>(`/geography/import/${jobId}/apply`)

export const cancelImport = (jobId: string) =>
  api.post<void>(`/geography/import/${jobId}/cancel`)

/** Register statistics import — `code,electionYear,registeredVoters,votesCast,…`. */
export const startStatisticsImport = (body: ImportRequest) =>
  api.post<ImportJobResponse>('/geography/import/statistics', body)

/** Bulk boundaries from one GeoJSON FeatureCollection, matched by a code property. */
export const startBoundaryImport = (body: BoundaryImportRequest) =>
  api.post<ImportJobResponse>('/geography/boundaries/import', body)

// ── Import template ─────────────────────────────────────────────────────────

/**
 * The filled CSV template. Guarded by `geography:import`, same as the import
 * itself — a template is only useful to someone who can upload one.
 *
 * Goes through `downloadFile` rather than `api.get`, because the response is
 * `text/csv` and the shared client parses every body as JSON.
 */
export const downloadImportTemplate = (
  kind: 'UNITS' | 'STATISTICS', unitType?: string, withExamples = true,
) =>
  downloadFile(
    url('/geography/import/template', { kind, unitType, withExamples }),
    kind === 'STATISTICS'
      ? 'axis-statistics-template.csv'
      : `axis-${(unitType ?? 'county').toLowerCase()}-template.csv`,
  )

/**
 * The same column definitions as JSON. Both endpoints read one `ImportTemplates`
 * class server-side, so the reference the UI renders cannot drift from the parser.
 */
export const importTemplateSpec = (kind: 'UNITS' | 'STATISTICS', unitType?: string) =>
  api.get<ImportTemplateSpec>(
    url('/geography/import/template/columns', { kind, unitType }),
  )

// ── Statistics ──────────────────────────────────────────────────────────────

/** Upsert on `(unit_id, election_year)`. Turnout is computed when omitted. */
export const recordStatistic = (id: string, electionYear: number, body: UnitStatisticRequest) =>
  api.put<void>(`/geography/units/${id}/statistics/${electionYear}`, body)

export const deleteStatistic = (id: string, electionYear: number) =>
  api.del<void>(`/geography/units/${id}/statistics/${electionYear}`)

// ── Intelligence overlay ────────────────────────────────────────────────────
//
// These exist so `intel` and the field features can publish onto a unit's row.
// The UI offers them behind `geography:overlay` — a separate permission from
// `geography:update`, because publishing a risk assessment and renaming a ward
// are different powers.

export const publishMood = (id: string, body: MoodAssessmentRequest) =>
  api.put<void>(`/geography/units/${id}/overlay/mood`, body)

export const publishRisk = (id: string, body: RiskAssessmentRequest) =>
  api.put<void>(`/geography/units/${id}/overlay/risk`, body)

export const publishAgent = (id: string, body: AgentAssignmentRequest) =>
  api.put<void>(`/geography/units/${id}/overlay/agent`, body)

/** `facet` is `mood`, `risk` or `agent`. */
export const clearOverlay = (id: string, facet: 'mood' | 'risk' | 'agent') =>
  api.del<void>(`/geography/units/${id}/overlay/${facet}`)

// ── Storage, for the import file ────────────────────────────────────────────

export const reserveUpload = (body: ReserveRequest) =>
  api.post<ReserveResponse>('/files/reserve', body)

/**
 * Mark a reserved file complete.
 *
 * `checksumSha256` is REQUIRED — it is `@NotBlank` on the server, and this function used
 * to post `{}`, so every confirm in the app 400'd with "must not be blank" while the bytes
 * sat uploaded but unconfirmed. Compute it with `sha256Hex` over the very blob you PUT.
 */
export const confirmUpload = (fileId: string, checksumSha256: string) =>
  api.post<unknown>(`/files/${fileId}/confirm`, { checksumSha256 })

export interface PresignedFile {
  url: string
  expiresAt: string
  contentType: string | null
  sizeBytes: number | null
  filename: string | null
}

/**
 * A short-lived presigned GET, as JSON, so the file can be rendered IN the page.
 *
 * NOT `/files/{id}/download`, which 302s to the object store: an `<img>`, `<audio>` or
 * `<video>` cannot carry the bearer token, so it never reaches the redirect. Fetch the URL
 * with the token, then point the element at the presigned link — which needs no auth of
 * its own. Five minutes, so re-presign rather than caching the URL.
 */
export const presignFile = (fileId: string) =>
  api.get<PresignedFile>(`/files/${fileId}/url`)

/**
 * Uploads straight to the presigned URL — NOT through `api`, which would attach
 * the bearer token and an `X-Request-Id` that the object store did not sign for
 * and would reject.
 */
export async function putToPresignedUrl(uploadUrl: string, file: File): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    body: file,
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
  })
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status}). The link may have expired — try again.`)
  }
}
